import React, { FC, useEffect, useState } from 'react';
import classNames from 'classnames';
import PaginationProvider from '@minecraft.pagination';
import { FlexCol, FlexRow, FlexStyleProps } from '@minecraft.atoms';
import { WindowWidthContext } from '@minecraft.utils';
import Select, { Option } from '@minecraft.select';
import { useTranslation } from '@blocs.i18n';

export type UsePaginationProps = {
  initialPageSize?: number;
  initialPageNumber?: number;
};

export const usePagination = ({ initialPageSize, initialPageNumber }: UsePaginationProps = {}) => {
  const [pageNumber, setPageNumber] = React.useState(initialPageNumber ?? 1);
  const [pageSize, setPageSize] = React.useState(initialPageSize ?? 100);

  return { pageNumber, pageSize, setPageNumber, setPageSize };
};

/**
 * A wrapper for pagination. This provides all the necessary components and display for pagination
 * of data from the backend.
 *
 * [Figma](https://www.figma.com/file/AiDAD1WpprgfbZI2XV6hU1/Casting-Networks-Design-System?node-id=340-3364&t=P8k2aDaWErPt5Ptv-0)
 * See "Pagination" section
 *
 * @example
 * const { pageNumber, pageSize, setPageNumber, setPageSize } = usePagination();
 * const { totalRecords } = useYourQueryResponse();
 *
 * return (
 * <Pagination pageNumber={pageNumber} pageSize={pageSize} setPageNumber={setPageNumber} setPageSize={setPageSize} totalRecords={totalRecords}>
 *   <p>The Children</p>
 * </Pagination>)
 *
 */

export interface SimplePaginationProps {
  pageNumber: number;
  pageSize: number;
  /**
   * Defaults to [25, 50, 100] as this is the typical page size for most things
   */
  pageSizeOptions?: number[];
  setPageNumber: (pageNumber: number) => void;
  setPageSize: (pageSize: number) => void;
  totalRecords: number;
  hideTopResultText?: boolean;
  hideBottomResultText?: boolean;
  hideSwitchers?: boolean;
  pageSizeOnMobile?: boolean;
  /**
   * Will hide the controls and output if the total records is 0
   */
  hideWhenEmpty?: boolean;
  loading?: boolean;
  flexGrow?: FlexStyleProps['grow'];
  ActionSlotComponent?: FC | React.ReactNode;
  sortOptionsOnMobile?: boolean;
  sortOptions?: Option[];
  sortValue?: string;
  handleSortEvent?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  showResponsiveSelect?: boolean;
}

export const SimplePagination: React.FC<SimplePaginationProps> = ({
  children,
  pageNumber,
  pageSize,
  setPageNumber,
  setPageSize,
  pageSizeOptions,
  totalRecords,
  hideBottomResultText = false,
  hideSwitchers = false,
  hideTopResultText = false,
  pageSizeOnMobile = false,
  hideWhenEmpty = false,
  loading,
  flexGrow = 1,
  ActionSlotComponent,
  sortOptionsOnMobile = false,
  sortOptions = [],
  sortValue = 0,
  handleSortEvent,
  showResponsiveSelect = false,
}) => {
  const isMobile = WindowWidthContext.useIsMobile();
  const { t } = useTranslation();
  const [hide, setHide] = useState(hideWhenEmpty && totalRecords === 0);

  // If the page number * the page size > total records, then we set the current page number to the last page
  // This is to prevent the user from being on a page that has no data
  useEffect(() => {
    if (totalRecords && pageNumber * pageSize - pageSize > totalRecords) {
      setPageNumber(Math.ceil(totalRecords / pageSize));
    }
  }, [pageNumber, pageSize, totalRecords, setPageNumber]);

  useEffect(() => {
    setHide(hideWhenEmpty && totalRecords === 0);
  }, [totalRecords, hideWhenEmpty]);

  return (
    <PaginationProvider
      totalRecords={totalRecords}
      defaultResultSizeOptions={pageSizeOptions ?? [25, 50, 100]}
      onPageChanged={(newPage) => {
        if (newPage.pageLimit !== pageSize && !Number.isNaN(newPage.pageLimit)) {
          setPageSize(Number(newPage.pageLimit));
          setPageNumber(1);
          window.scrollTo({ top: 0 });
        }

        if (newPage.currentPage !== pageNumber && !Number.isNaN(newPage.currentPage)) {
          setPageNumber(Number(newPage.currentPage));
          window.scrollTo({ top: 0 });
        }
      }}
      pageLimit={pageSize}
      currentPage={pageNumber}
    >
      {!(hide || hideTopResultText) && (
        <FlexRow
          justifyContent="space-between"
          className="cn_w-full"
          data-testid="simple-pagination-upper-results"
          grow={flexGrow}
        >
          <PaginationProvider.ResultText showResponsiveSelect={showResponsiveSelect} />
          {ActionSlotComponent}
        </FlexRow>
      )}
      {sortOptionsOnMobile && isMobile && (
        <FlexCol className="cn_w-full" data-testid="grid-pagination-sort-options">
          <Select
            className="cn_mt-4 cn_w-full"
            grow={1}
            value={sortValue}
            options={sortOptions}
            onChange={handleSortEvent}
            useFullWidth
          />
        </FlexCol>
      )}
      {children}
      {!hide && (
        <FlexRow justifyContent="space-between" className="cn_w-full" data-testid="simple-pagination-lower-results">
          {isMobile && !hideSwitchers && <PaginationProvider.Switcher pageSizeOnMobile={pageSizeOnMobile} />}
          {!isMobile && (
            <>
              {!hideBottomResultText && <PaginationProvider.ResultText loading={loading} />}
              {!hideSwitchers && (
                <PaginationProvider.Switcher
                  loading={loading}
                  className={classNames({ 'cn_w-full': hideBottomResultText })}
                />
              )}
            </>
          )}
        </FlexRow>
      )}
    </PaginationProvider>
  );
};
